// src/services/sensorService.js
import api from "./Api";

// Enum de tipo de señal (alineado con backend)
export const SignalType = {
  Analog: 0,
  OneWire: 1,
  Digital: 2,
  Uart: 3,
  I2c: 4,
};

// Bases relativas (Api.js resuelve dev/prod/https)
const BASE = "Sensores";
const CFG = "configuracion-tiempo";
const ESTANQUES = "/Estanques/mis-estanques";

// ----------------- Sensores (V1 compat y V2 nuevos) -----------------

// V1 (compat) - UART (PinRx/PinTx)
const crearSensor = async ({
  nombre,
  descripcion,
  dispositivoId,
  tipoParametroId,
  pinRx,
  pinTx,
  permisos,
  tiempoSensor,
}) => {
  const payload = {
    nombre,
    descripcion,
    dispositivoId,
    tipoParametroId,
    pinRx,
    pinTx,
    permisos,
    tiempoSensor,
  };
  const { data } = await api.post(`${BASE}/crear`, payload);
  return data; // { mensaje, sensorId }
};

// V2 - creación general por tipo de señal
// Espera: { nombre, descripcion?, dispositivoId, tipoParametroId, signalType, pinPrincipal?, pinRx?, pinTx?, busId?, unidad?, permisos?, tiempoSensor? }
const crearSensorV2 = async (sensor) => {
  const { data } = await api.post(`${BASE}/crear-v2`, sensor);
  return data; // { mensaje, sensorId }
};

const obtenerTodosSensores = async () => {
  const { data } = await api.cachedGet(`${BASE}/todos`);
  return data;
};

// (Opcional) si tu backend lo expone; si no, quítalo del UI.
const obtenerSensoresActivos = async (estanqueId) => {
  const { data } = await api.cachedGet(`${BASE}/todos`);
  return (data || [])
    .filter((estanque) => estanque.estanqueId === Number(estanqueId))
    .flatMap((estanque) => estanque.dispositivos || [])
    .flatMap((dispositivo) => dispositivo.sensores || [])
    .filter((sensor) => sensor.estaActivo);
};

// V1 (compat) - actualizar UART
const actualizarSensor = async (id, datos) => {
  await api.put(`${BASE}/actualizar/${id}`, datos);
};

// V2 - actualizar general (parcial o completo)
const actualizarSensorV2 = async (id, datos) => {
  await api.put(`${BASE}/actualizar-v2/${id}`, datos);
};

// Toggle activo (ya soportado en el controller nuevo)
const toggleActivoSensor = async (id) => {
  await api.put(`${BASE}/toggle-activo/${id}`, {});
};

// (Opcional) si tu backend lo expone; si no, quítalo del UI.
const obtenerMisSensores = async () => {
  const { data } = await api.cachedGet(`${BASE}/todos`);
  return (data || [])
    .flatMap((estanque) => estanque.dispositivos || [])
    .flatMap((dispositivo) => dispositivo.sensores || []);
};

const obtenerTiposSensor = async () => {
  const { data } = await api.cachedGet(`${BASE}/tipos`);
  return data;
};

const obtenerEstanques = async () => {
  const { data } = await api.cachedGet(ESTANQUES);
  return data;
};

// --------- Pines libres y capacidades ---------
const obtenerPinesLibres = async (dispositivoId, signalType /* int */, sensorId) => {
  const { data } = await api.get(`${BASE}/pines-libres`, {
    params: { dispositivoId, signalType, sensorId },
  });
  // => { dispositivoId, signalType, opciones: [{ pin, etiqueta, ocupado, riesgoso, motivo }] }
  return data;
};

const obtenerPinCapabilities = async () => {
  const { data } = await api.cachedGet(`${BASE}/pin-capabilities`);
  // => [{ gpio, esADC1, esADC2, soloEntrada, esBootStrap, esFlash, soportaOneWire, reservadoRol, etiqueta }]
  return data;
};

// (Opcional) útil si el firmware consulta tareas pendientes
const sincronizarAutomatico = async (codigo /* mac/code opcional */) => {
  const { data } = await api.get(`${BASE}/sincronizar-automatico`, {
    params: codigo ? { codigo } : {},
  });
  // => { total, sensores: [...] }
  return data;
};

// ------------- Configuración de Tiempo (Sensores) -------------
// GET: devuelve null si no hay configuración (204/404)
const obtenerTiempoSensor = async (sensorId) => {
  const resp = await api.get(`${CFG}/sensor/${sensorId}`, {
    validateStatus: (s) => (s >= 200 && s < 300) || s === 404 || s === 204,
  });
  if (resp.status === 204 || resp.status === 404) return null;
  return resp.data ?? null;
};

// POST/PUT podrían requerir superadmin según tu controller
const crearTiempoSensor = async ({ controladorId, dispositivoId, tiempoSensor }) => {
  const payload = {
    controladorId,
    dispositivoId,
    ciclos: false, // sensores usan TiempoSensor
    tiempoSensor: Number(tiempoSensor),
  };
  const { data } = await api.post(`${CFG}/sensor`, payload);
  return data;
};

const actualizarTiempoSensor = async (sensorId, { dispositivoId, tiempoSensor }) => {
  const payload = {
    controladorId: sensorId,
    dispositivoId,
    ciclos: false,
    tiempoSensor: Number(tiempoSensor),
  };
  const { data } = await api.put(`${CFG}/sensor/${sensorId}`, payload);
  return data;
};

const sensorService = {
  // V1 (compat)
  crearSensor,
  actualizarSensor,

  // V2
  crearSensorV2,
  actualizarSensorV2,

  // Pines / capacidades
  obtenerPinesLibres,
  obtenerPinCapabilities,

  // comunes
  obtenerTodosSensores,
  obtenerSensoresActivos, // ⚠ si tu API no lo tiene, quítalo del UI
  toggleActivoSensor,
  obtenerMisSensores, // ⚠ idem
  obtenerTiposSensor,
  obtenerEstanques,
  sincronizarAutomatico,

  // config tiempo
  obtenerTiempoSensor,
  crearTiempoSensor, // ⚠ puede requerir superadmin
  actualizarTiempoSensor, // ⚠ puede requerir superadmin
};

export default sensorService;
